import React, { useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { desktop, mobile } from '~/techstyle-shared/react-components';
import { ProductType } from '~/techstyle-shared/react-products';
import { useDomain } from '~/techstyle-shared/redux-core';

import { GRID_CARD_TYPES } from '../../constants';
import { useGridMeta } from '../../context/GridDataContexts';

import { RawDataDropdown, TreePreview } from './JsonTree';
import {
  CardItem,
  buildCioCollectionEditUrl,
  buildCioInteractUrl,
  buildCioSearchandizeUrl,
  getCioApiKey,
  resolveAssetEditUrl,
  resolveContainerEditUrl,
  resolveEsProductUrl,
  resolveProductEditUrl,
  resolveProductEditUrlById,
} from './urls';

// Solid 3px inner ring + soft outer halo, both in palette.blue. Reused for
// both :focus and the modal-open ($isActive) state below so the indicator is
// identical in either case.
const focusGlow = css`
  box-shadow:
    0 0 0 3px ${({ theme }) => theme.colors.palette.blue || '#5897FB'},
    0 0 12px 3px rgba(88, 151, 251, 0.7);
`;

const InfoButton = styled.button<{ $isActive?: boolean }>`
  position: absolute;
  top: 40px; /* shifted down to clear the marketing callout */
  left: 8px;
  z-index: 10;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 0;
  background: ${({ theme }) => theme.colors.backgroundDark};
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Only animate background — transitioning a multi-layer box-shadow makes
   * the inner solid ring appear before the outer glow expands. */
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.textDefault};
  }

  /* Show the glow whenever the button has focus — covers while the modal is
   * open, immediately after closing (focus is restored in handleClose), and
   * persists until the user clicks elsewhere or tabs away. :focus rather
   * than :focus-visible so a mouse click also triggers it. */
  &:focus {
    outline: none;
    ${focusGlow}
  }

  ${({ $isActive }) => $isActive && focusGlow}
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.backgroundModalOverlay};
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalPanel = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  width: 100%;
  max-width: 640px;
  max-height: 85vh;
  overflow-y: auto;
  border-radius: 8px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textDefault};

  ${desktop`
    max-width: min(1100px, 95vw);
  `}
`;

const ModalHeader = styled.header`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLine};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
`;

const ItemLabel = styled.span`
  color: ${({ theme }) =>
    theme.colors.palette.shadow65 || theme.colors.textSubdued};
  font-weight: 400;
  margin-left: 6px;
`;

const CloseButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  padding: 4px 8px;
  color: ${({ theme }) => theme.colors.textSubdued};

  &:hover {
    color: ${({ theme }) => theme.colors.textDefault};
  }
`;

const ModalBody = styled.div`
  padding: 16px 20px 20px;

  ${desktop`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 24px;
    align-items: start;
  `}
`;

// Left/right columns inside the modal body. On mobile this is just a
// regular block, so the columns stack naturally. On desktop the parent
// ModalBody becomes a 2-col grid and each column lands in its own track.
const ModalColumn = styled.div`
  min-width: 0;

  ${mobile`
    & + & {
      margin-top: 24px;
    }
  `}
`;

const Section = styled.section`
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.75px;
  color: ${({ theme }) => theme.colors.textDisabled};
`;

const KeyValueList = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, max-content) 1fr;
  column-gap: 16px;
  row-gap: 4px;
  margin: 0;

  dt {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textDefault};
  }

  dd {
    margin: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubdued};
    word-break: break-word;
  }
`;

const CmsLinkAnchor = styled.a`
  color: ${({ theme }) => theme.colors.palette.blue};
  text-decoration: underline;
  word-break: break-all;

  &:hover {
    color: ${({ theme }) => theme.colors.textDefault};
  }
`;

// External link wrapper that bypasses the GTM cross-domain auto-linker.
// GTM's global click listener mutates the `href` on cross-domain links
// (e.g. *.techstyle.tech) to append a `_gl=...` GA session-stitching param.
// For debug links that's just noise, so we navigate programmatically with
// the original href via `window.open` instead of letting the browser follow
// the (rewritten) anchor.
const CmsLink = ({
  href,
  children,
  onClick,
  ...rest
}: {
  href?: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
} & Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'children' | 'onClick'
>) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(event);
    }
    if (event.defaultPrevented || !href) {
      return;
    }
    // Preserve modifier-click / middle-click behavior — those open new tabs
    // via the browser's native handling, where GTM's rewrite is harmless.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  };
  return (
    <CmsLinkAnchor
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
      onClick={handleClick}
    >
      {children}
    </CmsLinkAnchor>
  );
};

const formatValue = (value: unknown): React.ReactNode => {
  if (value === null) {
    return <TreePreview>null</TreePreview>;
  }
  if (value === undefined) {
    return <TreePreview>undefined</TreePreview>;
  }
  if (value === '') {
    return <TreePreview>(empty)</TreePreview>;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const renderKeyValues = (record?: Record<string, unknown>) => {
  const entries = record ? Object.entries(record) : [];
  if (!entries.length) {
    return <TreePreview>No data</TreePreview>;
  }
  return (
    <KeyValueList>
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt>{key}</dt>
          <dd>{formatValue(value)}</dd>
        </React.Fragment>
      ))}
    </KeyValueList>
  );
};

const ExternalEditLink = ({
  href,
  label,
}: {
  href: string | null;
  label: string;
}) => {
  if (!href) {
    return null;
  }
  return (
    <>
      {' '}
      <CmsLink href={href}>{label} ↗</CmsLink>
    </>
  );
};

interface GridDebugProps {
  row: number;
  column: number;
  numGridColumns: number;
  item: CardItem;
}

const GridDebug = ({ row, column, numGridColumns, item }: GridDebugProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isAsset = item?.type === GRID_CARD_TYPES.ASSET;
  const isBundle = item?.productTypeId === ProductType.BUNDLE;
  const bundleComponents = isBundle
    ? (item?.componentProductIdObjectList ?? [])
    : [];
  const position = row * numGridColumns + column + 1;
  const assetEditUrl = resolveAssetEditUrl(item);
  const containerEditUrl = resolveContainerEditUrl(item);
  const productEditUrl = resolveProductEditUrl(item);
  const { tld } = useDomain();
  const {
    type: gridType,
    searchQuery,
    cioFilterName,
    cioFilterValue,
  } = useGridMeta();
  const cioApiKey = getCioApiKey(tld);
  const esProductUrl = resolveEsProductUrl(item);
  // Split the unified product into the slices we render as separate raw-data
  // dropdowns. Pricing and relatedProductsData are added by the merge step
  // (mergeUnifiedPrimaryProductsAndRelatedData); everything else is the
  // primary product feed (CIO or products/v2).
  const itemRecord = (item || {}) as Record<string, unknown>;
  const {
    pricing: pricingData,
    relatedProductsData,
    ...primaryData
  } = itemRecord;
  const cioProductInteractUrl =
    !isAsset && cioApiKey && item?.id != null && gridType !== 'products-v2'
      ? buildCioInteractUrl(cioApiKey, item.id)
      : null;
  const cioSearchInteractUrl =
    cioApiKey && gridType === 'cio-search' && searchQuery
      ? buildCioInteractUrl(cioApiKey, searchQuery)
      : null;
  const cioCollectionEditUrl =
    cioApiKey &&
    gridType === 'cio-browse' &&
    cioFilterName === 'collection_id' &&
    cioFilterValue
      ? buildCioCollectionEditUrl(cioApiKey, cioFilterValue)
      : null;
  const cioSearchandizeUrl =
    gridType === 'cio-browse' && cioFilterName && cioFilterValue
      ? buildCioSearchandizeUrl(cioApiKey || '', cioFilterName, cioFilterValue)
      : null;

  // Label the primary data dropdown with the actual source feeding the grid.
  const primaryDataSourceLabelByType: Record<typeof gridType, string> = {
    'cio-search': 'CIO Search',
    'cio-browse': 'CIO Browse',
    'products-v2': 'products/v2',
  };
  const primaryDataSourceLabel = primaryDataSourceLabelByType[gridType];

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  // Restore focus to the trigger so the focus ring re-anchors the user's
  // eye on the icon they came from after closing the modal.
  const handleClose = () => {
    setIsOpen(false);
    const button = buttonRef.current;
    if (button) {
      button.focus();
    }
  };

  const modal =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <Backdrop onClick={handleClose}>
            <ModalPanel onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>
                  {isAsset ? 'Asset Details' : 'Product Details'}
                  {item?.label && <ItemLabel>{item.label}</ItemLabel>}
                </h2>
                <CloseButton onClick={handleClose} aria-label="Close">
                  ×
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <ModalColumn>
                  <Section>
                    <SectionTitle>Grid Position</SectionTitle>
                    <KeyValueList>
                      <dt>Position</dt>
                      <dd>{position}</dd>
                      <dt>Row</dt>
                      <dd>{row + 1}</dd>
                      <dt>Col</dt>
                      <dd>{column + 1}</dd>
                      <dt>Width (cols)</dt>
                      <dd>{item?.span?.columns ?? 1}</dd>
                      <dt>Height (rows)</dt>
                      <dd>{item?.span?.rows ?? 1}</dd>
                    </KeyValueList>
                  </Section>

                  {isAsset && (
                    <>
                      <Section>
                        <SectionTitle>Asset</SectionTitle>
                        <KeyValueList>
                          <dt>ID</dt>
                          <dd>
                            {formatValue(item?.id)}
                            <ExternalEditLink
                              href={assetEditUrl}
                              label="Open in CMS"
                            />
                          </dd>
                          <dt>Name / Label</dt>
                          <dd>{formatValue(item?.label)}</dd>
                          <dt>Container</dt>
                          <dd>
                            {formatValue(item?.container?.name)}
                            <ExternalEditLink
                              href={containerEditUrl}
                              label="Open in CMS"
                            />
                          </dd>
                          {item?.imageFilename && (
                            <>
                              <dt>Image URL</dt>
                              <dd>
                                <CmsLink href={item.imageFilename}>
                                  {item.imageFilename}
                                </CmsLink>
                              </dd>
                            </>
                          )}
                        </KeyValueList>
                      </Section>

                      <Section>
                        <SectionTitle>Custom Vars (Raw from CMS)</SectionTitle>
                        {renderKeyValues(item?.options?.customVars)}
                      </Section>

                      <Section>
                        <SectionTitle>
                          Effective Custom Vars (Translated for Grid)
                        </SectionTitle>
                        {renderKeyValues(item?.effectiveCustomVars)}
                      </Section>
                    </>
                  )}

                  {!isAsset && (
                    <>
                      <Section>
                        <SectionTitle>
                          Product{isBundle ? ' (Bundle)' : ''}
                        </SectionTitle>
                        <KeyValueList>
                          <dt>Master Product ID</dt>
                          <dd>
                            {formatValue(item?.id)}
                            <ExternalEditLink
                              href={productEditUrl}
                              label="Open in Console"
                            />
                          </dd>
                          {isBundle && bundleComponents.length > 0 && (
                            <>
                              <dt>Component Product IDs</dt>
                              <dd>
                                {bundleComponents.map((component, index) => {
                                  const componentId =
                                    component?.componentProductId;
                                  if (componentId == null) {
                                    return null;
                                  }
                                  const href =
                                    resolveProductEditUrlById(componentId);
                                  return (
                                    <React.Fragment key={componentId}>
                                      {index > 0 && ', '}
                                      {href ? (
                                        <CmsLink href={href}>
                                          {componentId}
                                        </CmsLink>
                                      ) : (
                                        String(componentId)
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </dd>
                            </>
                          )}
                        </KeyValueList>
                      </Section>

                      <Section>
                        <SectionTitle>Data Sources</SectionTitle>
                        <KeyValueList>
                          <dt>ES Index</dt>
                          <dd>
                            {esProductUrl ? (
                              <CmsLink href={esProductUrl}>
                                Inspect product in ES ↗
                              </CmsLink>
                            ) : (
                              <TreePreview>unavailable</TreePreview>
                            )}
                          </dd>
                          {gridType === 'products-v2' && (
                            <>
                              <dt>Primary Source</dt>
                              <dd>Sourced from products/v2</dd>
                            </>
                          )}
                          {cioProductInteractUrl && (
                            <>
                              <dt>Constructor (Product)</dt>
                              <dd>
                                <CmsLink href={cioProductInteractUrl}>
                                  Inspect product in CIO ↗
                                </CmsLink>
                              </dd>
                            </>
                          )}
                          {cioSearchInteractUrl && (
                            <>
                              <dt>Constructor (Search)</dt>
                              <dd>
                                <CmsLink href={cioSearchInteractUrl}>
                                  Inspect search “{searchQuery}” in CIO ↗
                                </CmsLink>
                              </dd>
                            </>
                          )}
                          {cioCollectionEditUrl && (
                            <>
                              <dt>Constructor (Collection)</dt>
                              <dd>
                                <CmsLink href={cioCollectionEditUrl}>
                                  Edit collection “{cioFilterValue}” ↗
                                </CmsLink>
                              </dd>
                            </>
                          )}
                          {cioSearchandizeUrl && (
                            <>
                              <dt>Constructor (Searchandize)</dt>
                              <dd>
                                <CmsLink href={cioSearchandizeUrl}>
                                  Searchandize “{cioFilterValue}” ↗
                                </CmsLink>
                              </dd>
                            </>
                          )}
                        </KeyValueList>
                      </Section>
                    </>
                  )}
                </ModalColumn>

                <ModalColumn>
                  <Section>
                    <SectionTitle>
                      Raw {isAsset ? 'Asset' : 'Product'} Data
                    </SectionTitle>
                    <RawDataDropdown label="Full product data" value={item} />
                    {!isAsset && (
                      <>
                        <RawDataDropdown
                          label={`Primary data (${primaryDataSourceLabel})`}
                          value={primaryData}
                          emptyHint="No primary fields"
                        />
                        <RawDataDropdown
                          label="Pricing data (GQL)"
                          value={pricingData}
                          emptyHint="No pricing data on this product yet"
                        />
                        <RawDataDropdown
                          label="Related products (GQL)"
                          value={relatedProductsData}
                          emptyHint="No related products / group code data"
                        />
                      </>
                    )}
                  </Section>
                </ModalColumn>
              </ModalBody>
            </ModalPanel>
          </Backdrop>,
          document.body
        )
      : null;

  return (
    <>
      <InfoButton
        ref={buttonRef}
        type="button"
        $isActive={isOpen}
        onClick={handleOpen}
        aria-label="Show grid card debug info"
        title={`Pos ${position} — Row ${row + 1}, Col ${column + 1}`}
      >
        i
      </InfoButton>
      {modal}
    </>
  );
};

export default GridDebug;
